export type PermissionScope =
  | 'Employee'
  | 'Form'
  | 'FormSubmission'
  | 'Position'
  | 'Article'
  | 'Notification'
  | 'Reminder'
  | 'Letter'

export type PermissionDto = {
  id: number
  name: string
}

export type PermissionsByScope = Partial<
  Record<PermissionScope | string, PermissionDto[]>
>

export const HrPermission = {
  EmployeeReadAll: 'Employee.ReadAll',
  EmployeeReadOwn: 'Employee.ReadOwn',
  EmployeeUpdate: 'Employee.Update',
  EmployeeCreate: 'Employee.Create',
  EmployeeChangeStatus: 'Employee.ChangeStatus',
  EmployeeReadApplicant: 'Employee.ReadApplicant',
  EmployeeChangeApplicantStatus: 'Employee.ChangeApplicantStatus',
  FormCreate: 'Form.Create',
  FormReadAll: 'Form.ReadAll',
  FormReadApplication: 'Form.ReadApplication',
  FormReadEvaluation: 'Form.ReadEvaluation',
  FormUpdate: 'Form.Update',
  FormSubmissionCreate: 'FormSubmission.Create',
  FormSubmissionReadAll: 'FormSubmission.ReadAll',
  FormSubmissionReadOwn: 'FormSubmission.ReadOwn',
  PositionRead: 'Position.Read',
  ArticleCreate: 'Article.Create',
  ArticleRead: 'Article.Read',
  ArticleUpdate: 'Article.Update',
  ArticleDelete: 'Article.Delete',
  NotificationRead: 'Notification.Read',
  NotificationReadOwn: 'Notification.ReadOwn',
  NotificationCreate: 'Notification.Create',
  NotificationUpdate: 'Notification.Update',
  NotificationDelete: 'Notification.Delete',
  NotificationDeleteOwn: 'Notification.DeleteOwn',
  ReminderCreateForEmployees: 'Reminder.CreateForEmployees',
  ReminderReadOwn: 'Reminder.ReadOwn',
  ReminderCreateOwn: 'Reminder.CreateOwn',
  LetterReadOwn: 'Letter.ReadOwn',
  LetterCreateOwn: 'Letter.CreateOwn',
  LetterForward: 'Letter.Forward',
  LetterReply: 'Letter.Reply',
} as const

export type HrPermissionCode = (typeof HrPermission)[keyof typeof HrPermission]

export type PermissionCheckMode = 'any' | 'all'

export const MODULE_PERMISSIONS = {
  dashboard: [HrPermission.EmployeeReadOwn],
  profile: [HrPermission.EmployeeReadOwn],
  employmentForm: [HrPermission.FormReadApplication],
  employmentFormHrView: [HrPermission.FormReadAll],
  user: [HrPermission.EmployeeReadAll],
  secretariatModule: [
    HrPermission.LetterReadOwn,
    HrPermission.LetterCreateOwn,
    HrPermission.LetterForward,
    HrPermission.LetterReply,
  ],
  secretariatList: [HrPermission.LetterReadOwn],
  secretariatCreate: [HrPermission.LetterCreateOwn],
  secretariatRead: [HrPermission.LetterReadOwn],
  secretariatForward: [HrPermission.LetterForward],
  secretariatReply: [HrPermission.LetterReply],
  orgChart: [HrPermission.PositionRead],
  reminder: [
    HrPermission.ReminderReadOwn,
    HrPermission.ReminderCreateOwn,
    HrPermission.ReminderCreateForEmployees,
  ],
  recruitmentApplicants: [HrPermission.EmployeeReadApplicant],
  recruitmentFormList: [HrPermission.FormReadAll],
  recruitmentFormCreate: [HrPermission.FormCreate],
  recruitmentFormEdit: [HrPermission.FormUpdate],
  news: [HrPermission.NotificationRead],
  notificationCreate: [HrPermission.NotificationCreate],
  notificationUpdate: [HrPermission.NotificationUpdate],
  notificationDelete: [HrPermission.NotificationDelete],
  article: [HrPermission.ArticleRead],
  articleCreate: [HrPermission.ArticleCreate],
  articleUpdate: [HrPermission.ArticleUpdate],
  articleDelete: [HrPermission.ArticleDelete],
  evaluation: [HrPermission.FormReadAll],
  evaluationFormCreate: [HrPermission.FormCreate],
  evaluationFormEdit: [HrPermission.FormUpdate],
  evaluationAssigned: [HrPermission.FormReadEvaluation],
  evaluationMyPerformance: [HrPermission.FormReadEvaluation],
  evaluationSubmissions: [HrPermission.FormSubmissionReadAll],
} as const satisfies Record<string, readonly HrPermissionCode[]>

export type ModulePermissionKey = keyof typeof MODULE_PERMISSIONS
